import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { hudPaths, toHudHash } from '../content';

type ConsentDecision = 'accepted' | 'rejected';

const storageKey = 'dl_cookie_consent_v1';

const consoleLines = [
  'CONSENT MODULE BOOT',
  'Non-essential cookies require explicit user directive.',
  'Telemetry preference pending.',
  'Action required: ACCEPT or REJECT.'
];

const consoleScript = consoleLines.map((line) => `> ${line}`).join('\n');

const readConsent = (): ConsentDecision | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const value = window.localStorage.getItem(storageKey);
    return value === 'accepted' || value === 'rejected' ? value : null;
  } catch {
    return null;
  }
};

const persistConsent = (decision: ConsentDecision) => {
  try {
    window.localStorage.setItem(storageKey, decision);
  } catch {
    // Ignore storage failures; in-memory state still dismisses the banner for this session.
  }
};

export const CookieConsentBanner = () => {
  const reducedMotion = useReducedMotion();
  const [consent, setConsent] = useState<ConsentDecision | null>(() => readConsent());
  const [typedLength, setTypedLength] = useState(() => (reducedMotion ? consoleScript.length : 0));
  const [isMounted, setIsMounted] = useState(false);

  const visible = consent === null;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!visible || reducedMotion) {
      setTypedLength(consoleScript.length);
      return;
    }

    setTypedLength(0);
    const timer = window.setInterval(() => {
      setTypedLength((current) => {
        if (current >= consoleScript.length) {
          window.clearInterval(timer);
          return current;
        }
        return current + 2;
      });
    }, 11);

    return () => {
      window.clearInterval(timer);
    };
  }, [reducedMotion, visible]);

  const typedConsole = useMemo(() => consoleScript.slice(0, typedLength), [typedLength]);
  const typingComplete = typedLength >= consoleScript.length;

  const handleDecision = (decision: ConsentDecision) => {
    persistConsent(decision);
    setConsent(decision);
  };

  if (!isMounted) {
    return null;
  }

  return (
    <AnimatePresence>
      {visible ? (
        <motion.aside
          key="cookie-consent-banner"
          initial={{ opacity: 0, y: 30, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 22, scale: 0.98 }}
          transition={{ duration: 0.32, ease: [0.2, 1, 0.32, 1] }}
          className="fixed bottom-3 left-1/2 z-[75] w-[min(96vw,52rem)] -translate-x-1/2 overflow-hidden rounded-lg border border-brand-lime/35 bg-brand-panel/94 p-3 shadow-[0_0_30px_rgba(183,255,106,0.18)] backdrop-blur-sm sm:bottom-4 sm:p-4"
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent dialog"
        >
          <motion.div
            className="pointer-events-none absolute inset-y-0 left-[-24%] w-[18%] bg-[linear-gradient(90deg,transparent,rgba(183,255,106,0.2),transparent)]"
            animate={reducedMotion ? undefined : { x: ['0%', '620%'], opacity: [0, 0.95, 0] }}
            transition={{ duration: 3.8, repeat: Infinity, ease: 'linear', repeatDelay: 0.6 }}
          />

          <div className="relative z-10">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <p className="hud-micro text-brand-lime">PRIVACY CONTROL // CONSENT GATEWAY</p>
              <a
                href={toHudHash(hudPaths.cookiePolicy)}
                className="font-mono text-[0.62rem] uppercase tracking-[0.18em] text-brand-aqua transition hover:text-brand-text"
              >
                Open Cookie Policy
              </a>
            </div>

            <div className="rounded-md border border-brand-lime/28 bg-brand-panel-2/85 p-2.5 sm:p-3">
              <pre className="m-0 min-h-[4.9rem] whitespace-pre-wrap font-mono text-[0.64rem] uppercase leading-relaxed tracking-[0.11em] text-brand-text-2 sm:min-h-[5.4rem] sm:text-[0.68rem]">
                {typedConsole}
                {!typingComplete ? (
                  <motion.span
                    className="ml-0.5 inline-block h-[0.8rem] w-[0.4rem] bg-brand-lime align-middle"
                    animate={reducedMotion ? undefined : { opacity: [0.15, 1, 0.15] }}
                    transition={{ duration: 0.7, repeat: Infinity }}
                  />
                ) : null}
              </pre>
            </div>

            <div className="mt-3 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => handleDecision('rejected')}
                className="inline-flex items-center justify-center rounded-md border border-brand-aqua/45 bg-brand-aqua/10 px-4 py-2 font-mono text-[0.6rem] uppercase tracking-[0.2em] text-brand-aqua transition hover:border-brand-aqua hover:bg-brand-aqua/16 hover:text-brand-text"
              >
                Reject
              </button>
              <button
                type="button"
                onClick={() => handleDecision('accepted')}
                className="inline-flex items-center justify-center rounded-md border border-brand-lime/60 bg-brand-lime/15 px-4 py-2 font-mono text-[0.6rem] uppercase tracking-[0.2em] text-brand-lime transition hover:border-brand-lime hover:bg-brand-lime/24 hover:text-brand-text"
              >
                Accept
              </button>
            </div>
          </div>
        </motion.aside>
      ) : null}
    </AnimatePresence>
  );
};
